import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query';
import { jobsService } from '@/lib/api/services';
import type {
  Job,
  CreateJobInput,
  UpdateJobInput,
  CopyJobInput,
  ListJobsRequest,
  PaginatedResponse,
} from '@coi-live/shared-types';

/**
 * Hook for fetching a paginated list of projects.
 */
export function useJobs(params: ListJobsRequest = {}) {
  return useQuery({
    queryKey: queryKeys.jobs.list(params as Record<string, unknown>),
    queryFn: () => jobsService.list(params),
  });
}

/**
 * Hook for fetching a single project by ID.
 */
export function useJob(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.jobs.detail(id ?? ''),
    queryFn: () => jobsService.get(id!),
    enabled: !!id,
  });
}

/**
 * Hook for creating a new project.
 */
export function useCreateJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateJobInput) => jobsService.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.jobs.lists() });
    },
  });
}

/**
 * Hook for updating an existing project.
 */
export function useUpdateJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateJobInput }) =>
      jobsService.update(id, input),
    onSuccess: (updatedProject) => {
      queryClient.setQueryData(queryKeys.jobs.detail(updatedProject.id), updatedProject);
      queryClient.invalidateQueries({ queryKey: queryKeys.jobs.lists() });
    },
  });
}

/**
 * Hook for deleting a project with optimistic update.
 */
export function useDeleteJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => jobsService.delete(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.jobs.all });

      const previousLists = queryClient.getQueriesData<PaginatedResponse<Job>>({
        queryKey: queryKeys.jobs.lists(),
      });

      queryClient.setQueriesData<PaginatedResponse<Job>>(
        { queryKey: queryKeys.jobs.lists() },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            items: old.items.filter((project) => project.id !== id),
          };
        }
      );

      return { previousLists };
    },
    onError: (_err, _id, context) => {
      if (context?.previousLists) {
        context.previousLists.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.jobs.lists() });
    },
  });
}

/**
 * Hook for copying an existing job with its documents.
 */
export function useCopyJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ sourceJobId, input }: { sourceJobId: string; input: CopyJobInput }) =>
      jobsService.copy(sourceJobId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.jobs.lists() });
    },
  });
}

/**
 * Hook for setting or clearing the manual compliance override on a job.
 * After mutation, refetches the job so the UI immediately reflects the new
 * effective status (override → computed → null).
 */
export function useComplianceOverride() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      jobId,
      body,
    }: {
      jobId: string;
      body: { isCompliant?: boolean; reason?: string; clear?: boolean };
    }) => jobsService.setComplianceOverride(jobId, body),
    onSuccess: (updated) => {
      queryClient.setQueryData(queryKeys.jobs.detail(updated.id), updated);
      queryClient.invalidateQueries({ queryKey: queryKeys.jobs.lists() });
      // Activity log uses a non-namespaced key — see use-activity.ts
      queryClient.invalidateQueries({ queryKey: ['activity', updated.id] });
    },
  });
}
